// src/context/ScrapingContext.js
// Context to manage background email scraping state
// Uses sessionStorage to persist scanning state across page navigation

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { toast } from '../components/Toast';
import { scrapeOrderEmails } from '../services/emailScraper.service';

const ScrapingContext = createContext(null);

// Session storage keys
const STORAGE_KEYS = {
  IS_SCRAPING: 'nexus_scraping_active',
  PROGRESS: 'nexus_scraping_progress',
  RESULTS: 'nexus_scraping_results',
  SHOW_MODAL: 'nexus_scraping_show_modal',
};

// Helper to safely get from sessionStorage
const getSessionItem = (key, defaultValue = null) => {
  try {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to safely set to sessionStorage
const setSessionItem = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn('Failed to save to sessionStorage:', e);
  }
};

// Helper to remove from sessionStorage
const removeSessionItem = (key) => {
  try {
    sessionStorage.removeItem(key);
  } catch (e) {
    console.warn('Failed to remove from sessionStorage:', e);
  }
};

export const useScrapingContext = () => {
  const context = useContext(ScrapingContext);
  if (!context) {
    throw new Error('useScrapingContext must be used within a ScrapingProvider');
  }
  return context;
};

export const ScrapingProvider = ({ children }) => {
  // Initialize state from sessionStorage if available
  const [isScrapingInBackground, setIsScrapingInBackground] = useState(() =>
    getSessionItem(STORAGE_KEYS.IS_SCRAPING, false)
  );
  const [scrapingProgress, setScrapingProgress] = useState(() =>
    getSessionItem(STORAGE_KEYS.PROGRESS, {
      currentAccount: 0,
      totalAccounts: 0,
      currentAccountEmail: '',
      statusMessage: '', // "Searching...", "+3 orders", etc.
      // Running totals across all accounts
      runningTotals: {
        ordersFound: 0,
        trackingUpdates: 0,
      },
    })
  );
  const [lastResults, setLastResults] = useState(() =>
    getSessionItem(STORAGE_KEYS.RESULTS, null)
  );
  const [showCompletionModal, setShowCompletionModal] = useState(() =>
    getSessionItem(STORAGE_KEYS.SHOW_MODAL, false)
  );

  // Persist state changes to sessionStorage
  useEffect(() => {
    setSessionItem(STORAGE_KEYS.IS_SCRAPING, isScrapingInBackground);
  }, [isScrapingInBackground]);

  useEffect(() => {
    setSessionItem(STORAGE_KEYS.PROGRESS, scrapingProgress);
  }, [scrapingProgress]);

  useEffect(() => {
    // Only persist summary stats to sessionStorage, not full order arrays (avoids hitting 5MB limit)
    if (lastResults) {
      const lightweight = {
        ...lastResults,
        orders: [], // Don't persist full order objects
        cancelledOrders: [],
        shippedOrders: [],
        packages: [],
      };
      setSessionItem(STORAGE_KEYS.RESULTS, lightweight);
    } else {
      removeSessionItem(STORAGE_KEYS.RESULTS);
    }
  }, [lastResults]);

  useEffect(() => {
    setSessionItem(STORAGE_KEYS.SHOW_MODAL, showCompletionModal);
  }, [showCompletionModal]);

  // Ref to track if scrape was cancelled (checked between accounts)
  const cancelledRef = useRef(false);

  // Stop/cancel background scraping (stops between accounts)
  const stopBackgroundScrape = useCallback(() => {
    if (isScrapingInBackground) {
      cancelledRef.current = true;
      toast.info('Stopping scrape after current account...');
    }
  }, [isScrapingInBackground]);

  // Start background scraping
  const startBackgroundScrape = useCallback(async ({
    startDate,
    endDate,
    stores,
    connectionIds,
    connections,
    scanMode = 'both', // 'orders', 'shipping', or 'both'
    onComplete,
  }) => {
    if (isScrapingInBackground) {
      toast.info('A scrape is already in progress');
      return;
    }

    cancelledRef.current = false;

    setIsScrapingInBackground(true);
    setScrapingProgress({
      currentAccount: 0,
      totalAccounts: connectionIds.length,
      currentAccountEmail: '',
      statusMessage: 'Starting...',
      runningTotals: {
        ordersFound: 0,
        trackingUpdates: 0,
      },
    });

    const allResults = {
      found: 0,
      alreadyProcessed: 0,
      parsed: 0,
      cancelledCount: 0, // Orders updated to cancelled status
      shippedCount: 0, // Orders updated with shipping info
      skippedDueToLimit: 0, // Orders skipped because weekly limit was reached
      orders: [],
      cancelledOrders: [],
      shippedOrders: [],
      packages: [],
      errors: [],
      usage: null,
      wasCancelled: false,
      limitWarning: null,
    };

    try {
      for (let i = 0; i < connectionIds.length; i++) {
        // Check if scrape was cancelled
        if (cancelledRef.current) {
          allResults.wasCancelled = true;
          toast.info('Scrape stopped by user');
          break;
        }

        const connectionId = connectionIds[i];
        const conn = connections.find(c => c.id === connectionId);

        setScrapingProgress(prev => ({
          ...prev,
          currentAccount: i + 1,
          totalAccounts: connectionIds.length,
          currentAccountEmail: conn?.email || `Account ${i + 1}`,
          statusMessage: 'Searching...',
        }));

        try {
          // Track batch progress for this account
          let lastCreated = 0;
          let lastTracking = 0;

          const result = await scrapeOrderEmails({
            startDate,
            endDate,
            stores: stores?.length > 0 ? stores : undefined,
            connectionId,
            scanMode,
            provider: conn?.provider || 'gmail',
            onProgress: (batchProgress) => {
              // Calculate incremental updates since last callback
              const newOrders = (batchProgress.created || 0) - lastCreated;
              const newTracking = (batchProgress.tracking?.updated || 0) - lastTracking;

              lastCreated = batchProgress.created || 0;
              lastTracking = batchProgress.tracking?.updated || 0;

              // Update UI with incremental progress
              if (newOrders > 0 || newTracking > 0) {
                setScrapingProgress(prev => {
                  const updatedTotals = {
                    ordersFound: (prev.runningTotals?.ordersFound || 0) + newOrders,
                    trackingUpdates: (prev.runningTotals?.trackingUpdates || 0) + newTracking,
                  };

                  // Build status showing what was just found
                  let statusMsg = newOrders > 0 ? `+${newOrders} order${newOrders !== 1 ? 's' : ''}` : '';
                  if (newTracking > 0) {
                    statusMsg += statusMsg ? `, +${newTracking} tracking` : `+${newTracking} tracking`;
                  }

                  return {
                    ...prev,
                    statusMessage: statusMsg || 'Processing...',
                    runningTotals: updatedTotals,
                  };
                });
              }
            },
          });

          // Check again after the async call
          if (cancelledRef.current) {
            allResults.wasCancelled = true;
            toast.info('Scrape stopped by user');
            break;
          }

          allResults.found += result.found || 0;
          allResults.alreadyProcessed += result.alreadyProcessed || 0;
          allResults.parsed += result.parsed || 0;
          allResults.cancelledCount += result.cancelledCount || 0;
          allResults.shippedCount += result.shippedCount || 0;
          allResults.skippedDueToLimit += result.skippedDueToLimit || 0;
          if (result.orders) allResults.orders.push(...result.orders);
          if (result.cancelledOrders) allResults.cancelledOrders.push(...result.cancelledOrders);
          if (result.shippedOrders) allResults.shippedOrders.push(...result.shippedOrders);
          if (result.packages) allResults.packages.push(...result.packages);
          if (result.errors) allResults.errors.push(...result.errors);
          if (result.usage) allResults.usage = result.usage;
          if (result.limitWarning) allResults.limitWarning = result.limitWarning;

          // Update final status message for this account (totals already updated via onProgress)
          const foundCount = result.parsed || 0;
          const shippedCount = result.shippedCount || 0;
          const alreadyProcessedCount = result.alreadyProcessed || 0;

          // Build final status message for this account
          let statusMsg = '';
          if (foundCount > 0) {
            statusMsg = `+${foundCount} order${foundCount !== 1 ? 's' : ''}`;
          }
          if (shippedCount > 0) {
            statusMsg += statusMsg ? `, +${shippedCount} tracking` : `+${shippedCount} tracking`;
          }
          if (!statusMsg) {
            statusMsg = alreadyProcessedCount > 0 ? 'Already processed' : 'No new emails';
          }

          setScrapingProgress(prev => ({
            ...prev,
            statusMessage: statusMsg,
            // Don't update runningTotals here - already updated via onProgress callback
          }));
        } catch (err) {
          console.error(`Error scraping account ${connectionId}:`, err);
          allResults.errors.push(`${conn?.email || 'Account'}: ${err.message}`);
          setScrapingProgress(prev => ({
            ...prev,
            statusMessage: 'Error',
          }));
        }
      }

      // Flag orders with potential issues (missing price only - tracking is added later via Check Shipping)
      const flaggedOrders = (allResults.orders || []).filter(order => {
        const hasNoPrice = !order.order_total && order.order_total !== 0;
        return hasNoPrice;
      }).map(order => ({
        ...order,
        flags: {
          missingPrice: !order.order_total && order.order_total !== 0,
        }
      }));

      allResults.flaggedOrders = flaggedOrders;
      allResults.flaggedCount = flaggedOrders.length;

      setLastResults(allResults);

      // Only show completion modal if there are flagged items that need review
      // Otherwise just show a toast notification
      if (!allResults.wasCancelled) {
        if (flaggedOrders.length > 0) {
          // Show modal only when there are issues to review
          setShowCompletionModal(true);
        } else if (allResults.parsed > 0 || allResults.cancelledCount > 0 || allResults.shippedCount > 0) {
          // No issues - just show success toast
          let message = '';
          if (allResults.parsed > 0) {
            message += `Imported ${allResults.parsed} order${allResults.parsed > 1 ? 's' : ''}`;
          }
          if (allResults.shippedCount > 0) {
            if (message) message += ', ';
            message += `${allResults.shippedCount} tracking update${allResults.shippedCount > 1 ? 's' : ''}`;
          }
          if (allResults.cancelledCount > 0) {
            if (message) message += ', ';
            message += `${allResults.cancelledCount} cancelled`;
          }
          toast.success(message);
        } else if (allResults.alreadyProcessed > 0) {
          toast.info('All emails in this range were already processed');
        } else {
          toast.info('No order emails found in this date range');
        }

        // Show limit warning if orders were skipped
        if (allResults.skippedDueToLimit > 0) {
          toast.warning(
            `Weekly limit reached — ${allResults.skippedDueToLimit} order${allResults.skippedDueToLimit > 1 ? 's were' : ' was'} not processed. Upgrade your plan or wait for the weekly reset.`,
            { duration: 8000 }
          );
        }
      }

      if (onComplete) {
        onComplete(allResults);
      }
    } catch (err) {
      console.error('Background scrape error:', err);
      toast.error(err.message || 'Failed to scrape emails');
    } finally {
      setIsScrapingInBackground(false);
      setScrapingProgress({
        currentAccount: 0,
        totalAccounts: 0,
        currentAccountEmail: '',
        statusMessage: '',
        runningTotals: {
          ordersFound: 0,
          trackingUpdates: 0,
        },
      });
      cancelledRef.current = false;
    }
  }, [isScrapingInBackground]);

  // Close the completion modal and clear results to free memory
  const closeCompletionModal = useCallback(() => {
    setShowCompletionModal(false);
    setLastResults(null);
    removeSessionItem(STORAGE_KEYS.RESULTS);
  }, []);

  // Clear all scraping state (call when user wants to reset)
  const clearScrapingState = useCallback(() => {
    setIsScrapingInBackground(false);
    setScrapingProgress({
      currentAccount: 0,
      totalAccounts: 0,
      currentAccountEmail: '',
      statusMessage: '',
      runningTotals: {
        ordersFound: 0,
        trackingUpdates: 0,
      },
    });
    setLastResults(null);
    setShowCompletionModal(false);
    removeSessionItem(STORAGE_KEYS.IS_SCRAPING);
    removeSessionItem(STORAGE_KEYS.PROGRESS);
    removeSessionItem(STORAGE_KEYS.RESULTS);
    removeSessionItem(STORAGE_KEYS.SHOW_MODAL);
  }, []);

  return (
    <ScrapingContext.Provider
      value={{
        isScrapingInBackground,
        scrapingProgress,
        lastResults,
        showCompletionModal,
        startBackgroundScrape,
        stopBackgroundScrape,
        closeCompletionModal,
        clearScrapingState,
      }}
    >
      {children}
    </ScrapingContext.Provider>
  );
};

export default ScrapingContext;
